import React, { useState, useEffect } from "react";
import { Bar, Line, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import { UserIcon, ChartBarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { decryptData } from "../../utils/storageCrypto";
ChartJS.register(...registerables);

const NotificationsDashboard = () => {
  const [activities, setActivities] = useState([]);
  console.log(activities);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('sentAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterRole, setFilterRole] = useState('');
  const navigate = useNavigate();

  const getAllNotifications = process.env.REACT_APP_FORE_NOTIFY + `notifications/get-all-notifications?hubId=${sessionStorage.getItem('hubId')}`;

  useEffect(() => {
    const fetchAllNotifications = async () => {
        const encryptedToken = sessionStorage.getItem('token');
        const token = decryptData(encryptedToken);

        try {
          const response = await fetch(getAllNotifications, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-token': token,
            },
          });
      
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
      
          const data = await response.json();
          console.log('Response structure:', data);
          
          const notificationsArray = data.data || data || [];
          console.log('Notifications array:', notificationsArray);
          
          setActivities(notificationsArray);
          setLoading(false);
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
      };

    fetchAllNotifications();
  }, []);

  if (loading) return <div className="p-4 text-center"><div>Loading notifications...</div></div>;
  if (error) return <div className="p-4 text-center text-red-500"><div>Error: {error}</div></div>;

  const formatDateTime = (isoDate) => {
    const dateObj = new Date(isoDate);
    const date = dateObj.toLocaleDateString();
    const time = dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    });
    return `${date} ${time}`;
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Fixed filtering and sorting logic
  const filteredAndSortedActivities = activities
    .filter(activity => {
      if (!activity) return false;
      
      const recipientName = activity.recipientName || '';
      const roleName = activity.roleName || '';
      const message = activity.message || '';
      const how = activity.how || '';
      
      const searchLower = searchTerm.toLowerCase();
      
      const matchesSearch = recipientName.toLowerCase().includes(searchLower) ||
                           roleName.toLowerCase().includes(searchLower) ||
                           message.toLowerCase().includes(searchLower) ||
                           how.toLowerCase().includes(searchLower);
      
      const matchesRole = filterRole ? roleName === filterRole : true;
      
      return matchesSearch && matchesRole;
    })
    .sort((a, b) => {
      if (!a || !b) return 0;
      
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
      if (bValue == null) return sortDirection === 'asc' ? 1 : -1;
      
      // Convert to string for comparison if needed
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAndSortedActivities.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAndSortedActivities.length / itemsPerPage);

  // Get unique roles for filter dropdown
  const uniqueRoles = [...new Set(activities.map(activity => activity?.roleName).filter(Boolean))];

  return (
    <div>
      {currentItems.length > 0 ? (
        (sessionStorage.getItem('roleId') === '1' || sessionStorage.getItem('roleId') === '0') && (
          <div>
            <div className="relative">
              <div className="absolute inset-0 bg-[#F9F9FB] rounded-t-lg"></div>
              <div className="relative flex justify-between items-center p-4 border-b">
                <h2 className="text-lg font-semibold text-gray-800">Notifications</h2>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm text-blue-500 disabled:text-gray-300 hover:bg-blue-50 rounded transition-colors"
                  >
                    &lt;
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm text-blue-500 disabled:text-gray-300 hover:bg-blue-50 rounded transition-colors"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-2 sm:space-y-0 sm:space-x-4">
                <input
                  type="text"
                  placeholder="Search by recipient name, role, message, or type..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page when searching
                  }}
                  className="w-full sm:flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <select
                  value={filterRole}
                  onChange={(e) => {
                    setFilterRole(e.target.value);
                    setCurrentPage(1); // Reset to first page when filtering
                  }}
                  className="w-full sm:w-48 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Roles</option>
                  {uniqueRoles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              
              <div className="text-sm text-gray-600 mb-4">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredAndSortedActivities.length)} of {filteredAndSortedActivities.length} notifications
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-center border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('sentAt')}>
                        Date {sortField === 'sentAt' && (sortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('how')}>
                        Type {sortField === 'how' && (sortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('recipientName')}>
                         Recipient Name {sortField === 'recipientName' && (sortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                       <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('roleName')}>
                         Recipient Role {sortField === 'roleName' && (sortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('message')}>
                        Message {sortField === 'message' && (sortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((activity, index) => (
                      <tr key={activity?.id || index} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                        <td className="p-3 text-sm border">{formatDateTime(activity?.sentAt)}</td>
                        <td className="p-3 text-sm border">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            activity?.how === 'email' ? 'bg-blue-100 text-blue-800' :
                            activity?.how === 'sms' ? 'bg-green-100 text-green-800' :
                            activity?.how === 'push' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {activity?.how || 'N/A'}
                          </span>
                        </td>
                        <td className="p-3 text-sm border font-medium">{activity?.recipientName || 'N/A'}</td>
                        <td className="p-3 text-sm border">
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                            {activity?.roleName || 'N/A'}
                          </span>
                        </td>
                        <td className="p-3 text-sm border text-left max-w-xs truncate" title={activity?.message}>
                          {activity?.message || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="p-8 text-center text-gray-500">
          <div className="mb-4">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-300" />
          </div>
          <h3 className="text-lg font-medium mb-2">No Notifications Found</h3>
          <p className="text-sm">Notifications will appear here when available.</p>
        </div>
      )}
    </div>
  );
};

export default NotificationsDashboard;
